// ============================================================================
// bejaratFej.js — a BELÉPÉSI CÉL (hirdetmény) témájának FEJLÉCE (2026-10-26, a titkár #96 javaslata): azonnal látszik, hol van az ember.
//
// Csak abban a témában jelenik meg, amelyet az admin belépési célnak jelölt (config/home.landing) - akár belépéskor nyílt meg magától, akár a
// menüből lépett be valaki: ugyanaz a felirat. Tartalma: nagy "Hirdetmény" cím és alcím; (automatikus megnyitásnál) rövid magyarázat;
// két legalább 44 px magas gomb: "Tovább az egyes menükhöz" és "A legfrissebb tájékoztató olvasása"; az admin által kijelölt, legfeljebb 3
// "Most olvasásra ajánlott tájékoztató" (a meglévő hozzászólásra mutat, a törzsszöveget NEM másoljuk). A hirdetmény elolvasása önmagában nem
// jelent megerősítést (a megerősítési kérések külön, a "Tudomásul vettem" gombbal rögzülnek).
//
// ÖSSZECSUKHATÓ (v158, Tamás kérdése: "mi kell a hirdetmény bezárásához?"): az "Összecsukás" gombbal egysoros sávvá zsugorodik
// ("📢 Hirdetmény" + "Megnyitás" + "Tovább az egyes menükhöz"), így az olvasáshoz marad hely, de a "hol vagyok" jelzés megmarad (a titkár:
// ne tűnjön el egészen). Az állapotot a program tartja (belépéskor magától nyílt meg: kinyitva).
// ============================================================================

import { t } from "./i18n.js";
import { el } from "./dom.js";

/** Az ajánlott hozzászólás címkéje a néző nyelvén (v163): a mentéskor eltett fordítás első sora, különben az eredeti címke. */
export function cimkeNyelven(p, nyelv) {
  if (p && typeof p.customLabel === "string" && p.customLabel.trim() !== "") return p.customLabel; // az admin saját címkéje (v164)
  const f = p && p.labels && nyelv ? p.labels[nyelv] : null;
  return typeof f === "string" && f.trim() !== "" ? f : (p && p.label) || "";
}

/** Az aktuális téma a belépési cél? (szoba + téma azonosító egyezik) */
export function bejaratTema(model) {
  const l = model && model.homeLanding;
  return Boolean(l && l.roomId && l.topicId && model.currentRoomId === l.roomId && model.currentTopicId === l.topicId);
}

/**
 * @param {object} model { uiLang, homeLanding, homePinned: [{messageId,label,postNumber}], bejaratAuto, bejaratZarva, messages, currentRoomId, currentTopicId }
 * @param {object} handlers onOpenRooms(), onJumpPinned(messageId), onBejaratToggle(zarva: boolean)
 * @returns {HTMLElement|null}
 */
export function renderBejaratFej(model, handlers) {
  if (!bejaratTema(model)) return null;
  if (model.bejaratBezarva === true) return null; // egy "×"-szel teljesen bezárva (v171): a következő belépésig nem jelenik meg újra
  const uiLang = model.uiLang;
  const bezar = () => el("button", {
    type: "button", class: "text-btn bejarat-bezar", text: "×", title: t(uiLang, "admin.close"), "aria-label": t(uiLang, "admin.close"),
    onclick: () => handlers.onBejaratClose && handlers.onBejaratClose(),
  });

  // --- Összecsukott állapot: egysoros sáv (a "hol vagyok" jelzés és a menü-gomb megmarad)
  if (model.bejaratZarva === true) {
    const sav = el("section", { class: "bejarat-fej bejarat-osszecsukva", "aria-label": t(uiLang, "entry.title") });
    sav.appendChild(el("span", { class: "bejarat-sav-cim", text: "📢 " + t(uiLang, "entry.title") }));
    sav.appendChild(el("button", {
      type: "button", class: "text-btn bejarat-nyit", text: "▾ " + t(uiLang, "entry.expand"), "aria-expanded": "false",
      onclick: () => handlers.onBejaratToggle && handlers.onBejaratToggle(false),
    }));
    sav.appendChild(el("button", { type: "button", class: "neutral-btn compact bejarat-menuk bejarat-menuk-kicsi", text: t(uiLang, "home.toMenus"), onclick: () => handlers.onOpenRooms && handlers.onOpenRooms() }));
    sav.appendChild(bezar());
    return sav;
  }

  // A törölt (a betöltött üzenetek között törölt jelzésű) ajánlott hozzászólást nem mutatjuk.
  const torolt = new Set((model.messages || []).filter((m) => m && m.deleted === true).map((m) => m.messageId));
  const ajanlott = (Array.isArray(model.homePinned) ? model.homePinned : []).filter((p) => p && p.messageId && !torolt.has(p.messageId)).slice(0, 3);
  // A legújabb: a legnagyobb sorszámú (a sorszámmal nem rendelkező a végére).
  const legujabb = [...ajanlott].sort((a, b) => (Number(b.postNumber) || 0) - (Number(a.postNumber) || 0))[0] || null;

  const fej = el("section", { class: "bejarat-fej", "aria-label": t(uiLang, "entry.title") });
  const felso = el("div", { class: "bejarat-felso" });
  felso.appendChild(el("h2", { class: "bejarat-cim", text: "📢 " + t(uiLang, "entry.title") }));
  felso.appendChild(el("button", {
    type: "button", class: "text-btn bejarat-zar", text: "▴ " + t(uiLang, "entry.collapse"), "aria-expanded": "true",
    onclick: () => handlers.onBejaratToggle && handlers.onBejaratToggle(true),
  }));
  felso.appendChild(bezar());
  fej.appendChild(felso);
  fej.appendChild(el("p", { class: "bejarat-alcim", text: t(uiLang, "entry.subtitle") }));
  if (model.bejaratAuto === true) fej.appendChild(el("p", { class: "bejarat-auto hint", text: t(uiLang, "entry.autoOpened") }));

  const gombok = el("div", { class: "bejarat-gombok" });
  gombok.appendChild(el("button", { type: "button", class: "primary-btn bejarat-menuk", text: t(uiLang, "home.toMenus"), onclick: () => handlers.onOpenRooms && handlers.onOpenRooms() }));
  if (legujabb) {
    gombok.appendChild(el("button", { type: "button", class: "neutral-btn bejarat-legujabb", text: t(uiLang, "entry.readLatest"), onclick: () => handlers.onJumpPinned && handlers.onJumpPinned(legujabb.messageId) }));
  }
  fej.appendChild(gombok);

  if (ajanlott.length > 0) {
    const reszlet = el("details", { class: "bejarat-ajanlott" });
    reszlet.open = model.bejaratAuto === true;
    reszlet.appendChild(el("summary", { text: t(uiLang, "entry.pinnedTitle") }));
    const lista = el("ul", { class: "bejarat-lista" });
    for (const p of ajanlott) {
      const sor = el("li");
      sor.appendChild(el("button", {
        type: "button", class: "text-btn bejarat-hivatkozas",
        text: (Number.isInteger(p.postNumber) ? `#${p.postNumber} · ` : "") + (cimkeNyelven(p, model.contentLang) || t(uiLang, "entry.pinnedNoLabel")),
        onclick: () => handlers.onJumpPinned && handlers.onJumpPinned(p.messageId),
      }));
      lista.appendChild(sor);
    }
    reszlet.appendChild(lista);
    fej.appendChild(reszlet);
  }
  return fej;
}
